import React, { useEffect, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDarkMode } from "../contexts/DarkModeContext";
import DownloadButton from "./DownloadButton";
import { findChunkRanges } from "./chunkHighlight";
import { describeSource } from "./previewUtils";

// Highlight the cited chunk's text inside the rendered docx-preview DOM. DOCX
// chunks don't carry bounding-box coordinates the way PDF chunks do, so we lean
// on text matching: the shared matcher (see chunkHighlight.js) returns the flat
// character ranges of the chunk's one true location, and we wrap those ranges
// in <mark> elements. Returns the number of marks inserted; 0 means silent
// fallback (no visual highlight, modal renders normally).
function applyChunkHighlights(container, chunkContent) {
  if (!container || !chunkContent) return 0;

  // Build flat text + per-node offset map from the rendered DOM (the flat
  // coordinates the matcher's ranges are expressed in).
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const nodes = [];
  let flatText = "";
  let n = walker.nextNode();
  while (n) {
    nodes.push({ node: n, start: flatText.length });
    flatText += n.textContent;
    n = walker.nextNode();
  }
  if (nodes.length === 0) return 0;

  const merged = findChunkRanges(flatText, chunkContent);
  if (merged.length === 0) return 0;

  // Group sub-ranges per text node. For each node we may have multiple
  // disjoint marks; we wrap them all in a single DocumentFragment swap to
  // avoid invalidating later iteration.
  //
  // Two-pointer sweep: both `merged` (by .start) and `nodes` (DOM order /
  // ascending offset) are sorted, so we advance the node cursor past any node
  // that ends before the current range starts, and break out of the inner
  // loop once a node starts after the current range ends. This keeps the
  // total work O(nodes + ranges + overlaps) instead of O(nodes × ranges).
  const subByNode = new Map();
  let nodeStart = 0;
  for (const range of merged) {
    while (
      nodeStart < nodes.length &&
      nodes[nodeStart].start + nodes[nodeStart].node.textContent.length <= range.start
    ) {
      nodeStart += 1;
    }
    for (let i = nodeStart; i < nodes.length; i += 1) {
      const { node, start: nStart } = nodes[i];
      if (nStart >= range.end) break;
      const nLen = node.textContent.length;
      const localStart = Math.max(0, range.start - nStart);
      const localEnd = Math.min(nLen, range.end - nStart);
      if (localEnd <= localStart) continue;
      if (!subByNode.has(node)) subByNode.set(node, []);
      subByNode.get(node).push({ localStart, localEnd });
    }
  }

  let firstMark = null;
  let hits = 0;
  for (const [node, subs] of subByNode) {
    if (!node.parentNode) continue;
    subs.sort((a, b) => a.localStart - b.localStart);
    const text = node.textContent;
    const frag = document.createDocumentFragment();
    let cursor = 0;
    for (const { localStart, localEnd } of subs) {
      if (localStart > cursor) frag.appendChild(document.createTextNode(text.slice(cursor, localStart)));
      const mark = document.createElement("mark");
      mark.className = "discover-citation-hl";
      mark.style.backgroundColor = "rgba(255, 235, 59, 0.55)";
      mark.style.color = "inherit";
      mark.style.padding = "0";
      mark.textContent = text.slice(localStart, localEnd);
      frag.appendChild(mark);
      if (!firstMark) firstMark = mark;
      hits += 1;
      cursor = localEnd;
    }
    if (cursor < text.length) frag.appendChild(document.createTextNode(text.slice(cursor)));
    node.parentNode.replaceChild(frag, node);
  }

  if (firstMark) {
    // Defer the scroll so the browser has laid out the inserted marks.
    requestAnimationFrame(() => {
      firstMark.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }
  return hits;
}

// docx-preview is lazy-loaded so it doesn't bloat the main bundle for users
// who never click a DOCX citation. The whole render path is async: fetch →
// blob → dynamic import → renderAsync → DOM.
//
// fetchUrl is always the backend proxy — DOCX inline rendering needs raw
// bytes, and the cloud URL (storage.cloud.google.com, Drive viewer) doesn't
// expose them to the browser. The proxy stream is in-memory only.
//
// fullDocUrl points at the cloud URL so the user can open the file in its
// native viewer (Drive viewer / GCS / S3) via their own session.
export default function DocxPreviewModal({ docName, fetchUrl, downloadUrl, fullDocUrl, source, provider, chunkContent, authHeader, onClose }) {
  const { isDarkMode } = useDarkMode();
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    const renderDocx = async () => {
      try {
        // fetchUrl is always the same-origin backend proxy, so cookies are
        // useful for auth and CORS is a non-issue.
        const res = await fetch(fetchUrl, {
          credentials: "include",
          headers: authHeader ? { Authorization: authHeader } : undefined,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;

        const { renderAsync } = await import("docx-preview");
        if (cancelled || !containerRef.current) return;
        // Clear any prior render before re-running (defensive — modal re-mounts
        // on each open, but StrictMode double-invokes effects in dev).
        containerRef.current.innerHTML = "";
        await renderAsync(blob, containerRef.current, null, {
          className: "docx-rendered",
          inWrapper: true,
          ignoreLastRenderedPageBreak: true,
        });
        if (cancelled) return;
        // Text-based highlight: wrap matched spans in <mark>. Failures here
        // are non-fatal — modal still shows the document, just without the
        // yellow boxes (silent fallback).
        try {
          if (chunkContent) applyChunkHighlights(containerRef.current, chunkContent);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("docx highlight failed:", err);
        }
        setStatus("ready");
      } catch (e) {
        if (!cancelled) {
          setStatus("error");
          setErrMsg(e?.message || String(e));
        }
      }
    };
    renderDocx();
    return () => { cancelled = true; };
  }, [fetchUrl, chunkContent, authHeader]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const sourceLabel = describeSource(source, provider);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998] transition-opacity"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Preview of ${docName}`}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          className={`relative w-full max-w-5xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col ${
            isDarkMode ? "bg-slate-800" : "bg-white"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-3 border-b ${
            isDarkMode ? "border-slate-700" : "border-gray-200"
          }`}>
            <div className="flex-1 mr-4 min-w-0">
              <h3 className={`text-sm font-semibold truncate ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}>{docName}</h3>
              <div className={`text-xs mt-0.5 truncate ${
                isDarkMode ? "text-slate-400" : "text-gray-500"
              }`}>
                Inline preview &middot; full document hosted on {sourceLabel}
              </div>
              {(fullDocUrl || downloadUrl) && (
                <a
                  href={fullDocUrl || downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={fullDocUrl || downloadUrl}
                  className={`block text-[11px] mt-0.5 truncate font-mono underline ${
                    isDarkMode ? "text-sky-400 hover:text-sky-300" : "text-sky-600 hover:text-sky-700"
                  }`}
                >
                  {fullDocUrl || downloadUrl}
                </a>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={`p-1 rounded-lg transition-colors ${
                isDarkMode
                  ? "hover:bg-slate-700 text-slate-400 hover:text-slate-200"
                  : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="overflow-auto p-4 flex-1 min-h-[200px] bg-white text-black">
            {status === "loading" && (
              <div className="flex items-center justify-center py-16 text-gray-600 text-sm">
                Loading document…
              </div>
            )}
            {status === "error" && (
              <div className="text-red-700 text-sm p-4">
                Couldn't render this document.{" "}
                <DownloadButton
                  url={downloadUrl}
                  authHeader={authHeader}
                  filename={docName}
                  className="underline"
                />
                .
                {errMsg && <div className="text-xs mt-2 opacity-70 break-all">{errMsg}</div>}
              </div>
            )}
            <div ref={containerRef} className={status === "ready" ? "" : "hidden"} />
          </div>
        </div>
      </div>
    </>
  );
}

DocxPreviewModal.propTypes = {
  docName: PropTypes.string.isRequired,
  fetchUrl: PropTypes.string.isRequired,
  downloadUrl: PropTypes.string.isRequired,
  fullDocUrl: PropTypes.string,
  source: PropTypes.oneOf(["connector", "local", "proxy"]).isRequired,
  provider: PropTypes.string,
  chunkContent: PropTypes.string,
  authHeader: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};
